// Layer 3 (Transformations): the Updates tab's change queue. Pure functions
// only: no React, no engine mutations, no side effects.
//
// The queue carries what this route owns — a training module's drafted
// revision and a procedure's change events. The rule and FMEA proposals Paul's
// mock draws beside them already have an owner (Advisory › Action), so they
// appear here as one pointer line, never a second listing.

import { pendingFmeaReviewCount } from '@/versions/v1/modules/overview/Scene.transform';
import { allDocuments, documentById } from './seed';
import { documentSignoffs, pendingPublishEffects } from './TrainingCompliance.transform';

export type PendingChange =
  | {
      kind: 'revision';
      docId: string;
      title: string;
      summary: string;
      seat: string;
      reviewer: string;
      status: string;
      sourceCaseLabel: string;
    }
  | {
      kind: 'change';
      docId: string;
      title: string;
      revision: string;
      at: string;
      summary: string;
      /** '' when the event flags no document — the card then drops the `flags` segment. */
      flagsDocId: string;
      flagsTitle: string;
    };

type ChangeEntry = Extract<PendingChange, { kind: 'change' }>;

const MONTH_INDEX: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

/**
 * `'D MMM YYYY'` → a comparable epoch day. The seed's dates never sort
 * correctly as raw strings ('18 May 2026' vs '02 Apr 2026'), so every
 * comparison goes through here; an unparseable date sorts last.
 */
export function parsedAt(at: string): number {
  const match = /^(\d{1,2}) ([A-Za-z]{3}) (\d{4})$/.exec(at);
  if (!match) return Number.NEGATIVE_INFINITY;
  const month = MONTH_INDEX[match[2]!];
  if (month === undefined) return Number.NEGATIVE_INFINITY;
  return Date.UTC(Number(match[3]), month, Number(match[1]));
}

/**
 * Revisions first in seed order (a drafted revision carries no date), then
 * change events newest first — the same join `revisionContext` does inline for
 * one procedure, widened to every procedure.
 */
export function pendingChanges(): PendingChange[] {
  const revisions: PendingChange[] = [];
  const changes: ChangeEntry[] = [];

  for (const doc of allDocuments()) {
    if (doc.kind === 'training' && doc.pendingRevision) {
      revisions.push({
        kind: 'revision',
        docId: doc.id,
        title: doc.title,
        summary: doc.pendingRevision.diffSummary,
        seat: doc.pendingRevision.reviewSeat,
        reviewer: doc.pendingRevision.reviewerName,
        status: doc.pendingRevision.status,
        sourceCaseLabel: doc.pendingRevision.sourceCaseLabel,
      });
    }

    if (doc.kind === 'procedure') {
      for (const event of doc.changeEvents) {
        const flagged = documentById(event.flagsTrainingId);
        changes.push({
          kind: 'change',
          docId: doc.id,
          title: doc.title,
          revision: event.revision,
          at: event.at,
          summary: event.summary,
          flagsDocId: flagged?.id ?? '',
          flagsTitle: flagged?.title ?? '',
        });
      }
    }
  }

  changes.sort((a, b) => parsedAt(b.at) - parsedAt(a.at));

  return [...revisions, ...changes];
}

/**
 * The rule and FMEA proposals are Advisory › Learn's fact; this route carries
 * the count and the pointer, never the rows. `pendingFmeaReviewCount` is the
 * single owner of that filter — imported, not re-derived.
 */
export function advisoryPendingLine(): { count: number; text: string; lead: string; target: string } {
  const count = pendingFmeaReviewCount();
  const target = 'Advisory › Learn';
  const lead = `${count} rule and FMEA proposal${count === 1 ? '' : 's'} await${count === 1 ? 's' : ''} review in `;
  return { count, text: `${lead}${target}`, lead, target };
}

/**
 * What each queued change does to the sign-off register (comp `documents.html`
 * B, the right column of each change): a pending revision opens a re-sign for
 * everyone current on the published version once it publishes; a published
 * procedure revision has left every older signature stale since.
 */
export type PendingChangeEffect = {
  kind: 'on-publish' | 'since-publish';
  count: number;
  people: Array<{ name: string; panel: string }>;
};

export function pendingChangeEffect(change: PendingChange): PendingChangeEffect | undefined {
  if (change.kind === 'revision') {
    const effect = pendingPublishEffects().find((row) => row.documentId === change.docId);
    const people = (effect?.resign ?? []).map(({ name, panel }) => ({ name, panel }));
    return { kind: 'on-publish', count: people.length, people };
  }
  // Only the published revision leaves signatures stale; an older event has no live effect.
  if (documentById(change.docId)?.version !== change.revision) return undefined;
  const stale = documentSignoffs(change.docId)?.stale ?? [];
  return {
    kind: 'since-publish',
    count: stale.length,
    people: stale.map(({ name, panel }) => ({ name, panel })),
  };
}
